import { generateClient, GraphQLSubscription } from 'aws-amplify/api';
import { DocumentNode, print } from 'graphql';

const MAX_CONSECUTIVE_RETRIES = 5;
const RETRY_BACKOFF_BASE_MS = 2000;
const RETRY_BACKOFF_MULTIPLIER = 2;

// Subscribes to a single document with its own fully isolated retry lifecycle (retry count, retry
// timer, current subscription handle) so two documents subscribed side by side never share or
// interfere with each other's state. Returns a cleanup function scoped to just this subscription.
export const subscribeToDocument = <TData = Record<string, unknown>>(
  document: DocumentNode,
  variables: Record<string, unknown>,
  getToken: () => Promise<string>,
  onEvent: (data: TData) => void,
  onGiveUp?: () => void
) => {
  // Created here, not at module level, so it runs after Amplify.configure() in index.tsx; retries reuse it.
  const client = generateClient();
  let isActive = true;
  let retryCount = 0;
  let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;
  let currentSubscription: { unsubscribe: () => void } | undefined;

  // Shared by both failure sources below (a rejected getToken() and a subscription `error`) so a
  // temporary token-acquisition failure consumes/respects the exact same bounded backoff as a
  // subscription-level error, instead of having its own separate retry path.
  const scheduleRetry = () => {
    if (!isActive) return;
    retryCount += 1;
    if (retryCount > MAX_CONSECUTIVE_RETRIES) {
      console.error('[subscribeToDocument] giving up after repeated errors', document);
      onGiveUp?.();
      return;
    }
    retryTimeoutId = setTimeout(attempt, RETRY_BACKOFF_BASE_MS * RETRY_BACKOFF_MULTIPLIER ** (retryCount - 1));
  };

  const attempt = async () => {
    if (!isActive) return; // guard: cleanup already ran before this attempt started

    let authToken: string;
    try {
      authToken = await getToken();
    } catch {
      // getToken() rejecting (e.g. a transient MSAL/Cognito failure) must not become an unhandled
      // rejection or silently stop recovery — treat it exactly like a subscription error.
      scheduleRetry();
      return;
    }
    if (!isActive) return; // guard again: cleanup could run during the `getToken()` await

    currentSubscription = client
      .graphql<GraphQLSubscription<Record<string, unknown>>>({
        query: print(document),
        variables,
        authMode: 'lambda',
        authToken
      })
      .subscribe({
        next: ({ data }) => {
          retryCount = 0; // any successful message resets this document's backoff counter
          onEvent(data as TData);
        },
        error: () => {
          if (!isActive) return;
          scheduleRetry();
        }
      });
  };

  attempt();

  return () => {
    isActive = false;
    if (retryTimeoutId) clearTimeout(retryTimeoutId);
    currentSubscription?.unsubscribe(); // only ever the single current handle — nothing accumulates
  };
};
